import React, { useEffect, useState } from 'react';
import { CircularProgress, IconButton, makeStyles, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import allActions from '../../../actions';
import { ReportAPI } from '../../../API/report';
import { unoTheme } from '../../../assets/styles/unoTheme';
import UnoButton from '../../utils/UnoButton';
import MyTable from '../../utils/MyTable';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { downloadBlob } from '../../../controllers/ExcelController';
import { getMonths } from '../../utils/utils';
import MySelect2 from '../../utils/MySelect2';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        '& .MuiTableContainer-root': {
            height: 'calc(100vh - 103px) !important',
        }
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(3),
        borderBottom: 'solid 1px rgba(0, 0, 0, 0.12)',
        backgroundColor: unoTheme.hoverTable,
        position: 'relative',
    },
    headerContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
    },
    title: {
        fontSize: '20px',
        fontWeight: 'bold',
        color: '#1C2228'
    },
    subtitle: {
        fontSize: '14px',
        color: unoTheme.secondaryColor,
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(2),
        top: '50%',
        transform: 'translateY(-50%)',
    },
    rightDivBackdrop: {
        zIndex: 3,
        backgroundColor: 'white',
        position: 'absolute',
        opacity: 0.85,
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,
        display: 'flex',
        flexDirection: 'column',
        color: unoTheme.mainColor,
        alignItems: 'center',
        justifyContent: 'center',
        '& .progress': {
            color: unoTheme.mainColor
        }
    },
}));

const MONTHS_NAMES = [
    '', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

export default function BackofficeReportHistory({ content }) {
    const dispatch = useDispatch();
    const classes = useStyles();

    const { data: { year } } = content;

    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState([]);
    const [reportMonth, setReportMonth] = useState(new Date().getMonth());

    const loadHistory = async () => {
        setLoading(true);
        const response = await ReportAPI.getBackofficeReportHistory(year);
        if (response.success && response.body && response.body.rows) {
            setHistory(response.body.rows);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadHistory();
    }, [year]);

    const handleClose = () => {
        dispatch(allActions.rightContentActions.setRightContent(null));
    };

    const handleDownload = async (reportId) => {
        const response = await ReportAPI.downloadBackofficeReport(reportId);
        if (response.success && response.body && response.body.blob) {
            downloadBlob(response.body.blob, response.body.fileName || 'relatorio_backoffice.xlsx');
        }
    };

    return (
        <div className={classes.root} style={{ position: 'relative' }}>

            <div className={classes.header}>
                <div className={classes.headerContent}>
                    <span className={classes.title}>Histórico de Relatórios</span>
                    <span className={classes.subtitle}>Relatórios do Backoffice - {year}</span>
                    <MySelect2
                        style={{ marginTop: '8px', minWidth: '120px' }}
                        id={'history-month-select'}
                        value={reportMonth}
                        onChange={(e) => setReportMonth(parseInt(e.target.value))}
                        options={getMonths().map((row, index) => (
                            <option key={'hist-month-' + index} value={index}>
                                {row}
                            </option>
                        ))}
                    />
                </div>
                <IconButton className={classes.closeButton} onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </div>

            {loading ? (
                <div className={classes.rightDivBackdrop}>
                    <CircularProgress className="progress" />
                </div>
            ) : history.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center' }}>
                    <Typography variant="body1" color="textSecondary">
                        Nenhum relatório gerado para {year}.
                    </Typography>
                </div>
            ) : (
                <MyTable
                    stickyHeader
                    size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell align="left">Mês/Ano</TableCell>
                            <TableCell align="left">Gerado por</TableCell>
                            <TableCell align="left">Data de geração</TableCell>
                            <TableCell align="center">Ação</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {history.filter(rh => rh.month === reportMonth + 1).map((rh, idx) => (
                            <TableRow key={'rh-' + idx} hover>
                                <TableCell align="left">
                                    {MONTHS_NAMES[rh.month] || rh.month}/{rh.year}
                                </TableCell>
                                <TableCell align="left">
                                    {rh.generated_by_name || '-'}
                                </TableCell>
                                <TableCell align="left">
                                    {rh.created_date ? moment(rh.created_date).format('DD/MM/YYYY [às] HH:mm') : '-'}
                                </TableCell>
                                <TableCell align="center">
                                    <UnoButton
                                        size="small"
                                        startIcon={<GetAppIcon style={{ fontSize: 16 }} />}
                                        onClick={() => handleDownload(rh.id)}
                                        type='primaryClean'>
                                        Baixar
                                    </UnoButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </MyTable>
            )}
        </div>
    );
}
